import { defineStore } from 'pinia';
import {
  setSupplierInfo,
  setClientInfo,
  clear,
  getSupplierInfo,
  getClientInfo,
} from '@/store/modules/supplier/auth';
import { clearUserCid, clearUserInfo } from '@/utils/auth';
import { removeRouteListener } from '@/utils/route-listener';
import { isElectron } from '@/utils/electronFunc';
import { removeScrollListener } from '@/utils/scroll-position-listener';
import { SupplierState } from './types';

const supplierInfo = getSupplierInfo();
const clientInfo = getClientInfo();

const useSupplierStore = defineStore('supplier', {
  state: (): SupplierState => ({
    // 加工厂登录返回的信息
    supplierInfo: supplierInfo || {},
    // 当前品牌方信息
    clientInfo: clientInfo || {},
    configMap: {},
    sysConfigList: [],
  }),

  getters: {
    // 用于判断当前登录的是否是加工厂账号
    isLoginSupplier(state: SupplierState) {
      return !!state.supplierInfo.token && !!state.clientInfo.supplierId;
    },
    // 是否展示生产批次
    hasProduceBatch(state: SupplierState) {
      const configList: any = state.sysConfigList;
      if (!Array.isArray(configList)) {
        return false;
      }
      return !!configList.find(
        (item: any) => item.configKey === 'produceBatchSnGenerate'
      )?.enable;
    },
    // 获取生产单统计列表查看的数据权限
    getProductStatisticColumn(state: SupplierState) {
      const configList: any = state.sysConfigList;
      if (!Array.isArray(configList)) {
        return [];
      }
      return configList.filter((item: any) =>
        item.configKey.startsWith('data:')
      );
    },
    // 获取生产单自定义字段权限
    getProduceCustomAttData(state: SupplierState) {
      const configList: any = state.sysConfigList;
      if (!Array.isArray(configList)) {
        return [];
      }
      return configList.filter((item: any) =>
        item.configKey.startsWith(`custom:PRODUCE_ORDER`)
      );
    },
    // 物料小数位
    materialDecimal(state: SupplierState): number {
      let materialDecimalVal = 2;
      state.sysConfigList.forEach((item: Record<string, any>) => {
        if (item.configKey === 'materialDecimal' && item.configValue) {
          materialDecimalVal = Number(item.configValue);
        }
      });
      return materialDecimalVal;
    },
    // 允许快麦预约入库，默认不开启
    isAllowKmPreIn(state: SupplierState): number {
      let configValue = 0;
      state.sysConfigList.forEach((item: Record<string, any>) => {
        if (item.configKey === 'allowKmPreIn' && item.configValue) {
          configValue = Number(item.configValue);
        }
      });
      return configValue;
    },
    // 同步快麦
    syncKmType(state: SupplierState): number {
      let configValue = 1;
      state.sysConfigList.forEach((item: Record<string, any>) => {
        if (item.configKey === 'syncKmType' && item.configValue) {
          configValue = Number(item.configValue);
        }
      });
      return configValue;
    },
  },

  actions: {
    getLoginSupplier() {
      return (
        !!this.$state.supplierInfo.token && !!this.$state.clientInfo.supplierId
      );
    },
    switchClient(client: Recordable = {}) {
      this.clientInfo = client;
      setClientInfo(client);
    },
    setSysConfigList(configList: Array<any> = []) {
      const sysConfigMap: Recordable = {};
      if (Array.isArray(configList)) {
        configList.forEach((config: any) => {
          const { configKey, configValue } = config;
          sysConfigMap[configKey] = configValue;
          if (['goodsSnGenerate', 'designSnGenerate'].includes(configKey)) {
            sysConfigMap[`${configKey}Enable`] = Number(config.enable);
          }
        });
      }
      this.configMap = sysConfigMap;
    },
    // Set user's information
    setInfo(partial: Partial<SupplierState>) {
      this.$patch(partial);
      if (partial.sysConfigList) this.setSysConfigList(partial.sysConfigList);
    },
    // Reset user's information
    resetInfo() {
      this.$reset();
      this.clientInfo = {};
      this.supplierInfo = {};
    },
    // Login
    async loginCallback(data: any) {
      try {
        this.supplierInfo = data;
        setSupplierInfo(data);
        const { assistantSupplierList = [] } = data;
        this.switchClient(assistantSupplierList[0]);
        clearUserCid(); // 删除erp用户cid缓存
        clearUserInfo(); // 删除erp登录用户缓存
      } catch (err) {
        clear();
        throw err;
      }
    },
    logoutCallBack() {
      clear();
      this.resetInfo();
      removeRouteListener();
      removeScrollListener();
    },
    // Logout
    logout() {
      this.logoutCallBack();
      if (isElectron()) {
        setTimeout(() => {
          window.localStorage.clear();
          window.location.reload(); // 刷新当前页面
        }, 200);
        return;
      }
      window.location.reload();
    },
  },
});

export default useSupplierStore;
